import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Tabs, Tab, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import { FaDownload, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaLock, FaCheckCircle } from 'react-icons/fa';

const StudyMaterials = () => {
  const [freeFiles, setFreeFiles] = useState([]);
  const [paidFiles, setPaidFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('free');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [purchasedFiles, setPurchasedFiles] = useState([]);
  const [error, setError] = useState('');
  const storage = getStorage();

  // Load purchased files from localStorage on component mount
  useEffect(() => {
    fetchFiles();
    const purchased = JSON.parse(localStorage.getItem('purchasedFiles') || '[]');
    setPurchasedFiles(purchased);
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      // Fetch free files
      const freeRef = ref(storage, 'downloads/free');
      const freeList = await listAll(freeRef).catch(() => ({ items: [] }));
      
      const freeFilesData = await Promise.all(
        freeList.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const pathParts = item._location.path_.split('/');
          const fileName = pathParts[pathParts.length - 1];
          const metadata = fileName.split('_');
          return {
            name: fileName.replace(/^(free|paid)_\d+_/, ''),
            url: url,
            timestamp: parseInt(metadata[1]),
            type: 'free',
            fullPath: item.fullPath
          };
        })
      );
      
      // Fetch paid files
      const paidRef = ref(storage, 'downloads/paid');
      const paidList = await listAll(paidRef).catch(() => ({ items: [] }));
      
      const paidFilesData = await Promise.all(
        paidList.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const pathParts = item._location.path_.split('/');
          const fileName = pathParts[pathParts.length - 1];
          const metadata = fileName.split('_');
          return {
            name: fileName.replace(/^(free|paid)_\d+_/, ''),
            url: url,
            timestamp: parseInt(metadata[1]),
            type: 'paid',
            fullPath: item.fullPath,
            price: 100 // Default price in INR
          };
        })
      );
      
      // Sort files by timestamp (newest first)
      freeFilesData.sort((a, b) => b.timestamp - a.timestamp);
      paidFilesData.sort((a, b) => b.timestamp - a.timestamp);
      
      setFreeFiles(freeFilesData);
      setPaidFiles(paidFilesData);
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to load study materials. Please try again later.');
    }
    setLoading(false);
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf size={32} color="#dc3545" />;
      case 'doc':
      case 'docx': return <FaFileWord size={32} color="#0d6efd" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel size={32} color="#198754" />;
      default: return <FaFile size={32} color="#6c757d" />;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const initiatePayment = async (file) => {
    setSelectedFile(file);
    setShowPaymentModal(true);
    setPaymentSuccess(false);
    setError('');
  };

  const handlePayment = async () => {
    if (!userName || !userEmail) {
      setError('Please enter your name and email');
      return;
    }

    setError('');
    
    try {
      // Create order on your server
      const orderResponse = await fetch('https://your-backend.com/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: selectedFile.price * 100, // Amount in paise
          currency: 'INR',
          receipt: `receipt_${selectedFile.timestamp}`,
        }),
      });

      const orderData = await orderResponse.json();

      // Razorpay options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Your Razorpay Key ID
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Study Materials Premium',
        description: `Purchase: ${selectedFile.name}`,
        image: 'https://your-website.com/logo.png',
        order_id: orderData.id,
        handler: async function (response) {
          // Verify payment on your server
          const verificationResponse = await fetch('https://your-backend.com/verify-payment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });

          const verificationData = await verificationResponse.json();

          if (verificationData.success) {
            // Payment successful
            setPaymentSuccess(true);
            
            // Add to purchased files
            const updatedPurchasedFiles = [...purchasedFiles, selectedFile.fullPath];
            setPurchasedFiles(updatedPurchasedFiles);
            localStorage.setItem('purchasedFiles', JSON.stringify(updatedPurchasedFiles));
            
            // Send confirmation email (you would implement this on your backend)
            await fetch('https://your-backend.com/send-confirmation', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: userEmail,
                fileName: selectedFile.name,
                downloadLink: selectedFile.url,
              }),
            });
          } else {
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
        },
        notes: {
          file: selectedFile.name,
        },
        theme: {
          color: '#007bff',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment initialization failed. Please try again.');
    }
  };

  const isFilePurchased = (filePath) => {
    return purchasedFiles.includes(filePath);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4" style={{ color: '#1976d2', fontWeight: 700 }}>Study Materials</h2>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
        fill
      >
        <Tab eventKey="free" title={
          <span>
            Free Materials <Badge bg="success">{freeFiles.length}</Badge>
          </span>
        }>
          <div className="mt-3">
            {freeFiles.length === 0 ? (
              <Card className="text-center p-5">
                <Card.Body>
                  <h4>No free study materials available.</h4>
                  <p className="text-muted">Check back later for updates</p>
                </Card.Body>
              </Card>
            ) : (
              <Row xs={1} md={2} lg={3} className="g-4">
                {freeFiles.map((file, index) => (
                  <Col key={index}>
                    <Card className="h-100 shadow-sm hover-shadow" 
                      style={{ 
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        cursor: 'pointer',
                        borderRadius: '12px'
                      }}
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-3">
                          {getFileIcon(file.name)}
                          <div className="ms-3">
                            <Card.Title className="mb-0" style={{ fontSize: '1.1rem' }}>
                              {file.name}
                            </Card.Title>
                            <small className="text-muted">Uploaded: {formatDate(file.timestamp)}</small>
                          </div>
                        </div>
                        <Button
                          variant="primary"
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-auto"
                          style={{ borderRadius: '8px' }}
                        >
                          <FaDownload className="me-2" /> Download
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Tab>
        
        <Tab eventKey="paid" title={
          <span>
            Premium Materials <Badge bg="warning">{paidFiles.length}</Badge>
          </span>
        }>
          <div className="mt-3">
            {paidFiles.length === 0 ? (
              <Card className="text-center p-5">
                <Card.Body>
                  <h4>No premium study materials available.</h4>
                  <p className="text-muted">Check back later for updates</p>
                </Card.Body>
              </Card>
            ) : (
              <Row xs={1} md={2} lg={3} className="g-4">
                {paidFiles.map((file, index) => {
                  const isPurchased = isFilePurchased(file.fullPath);
                  
                  return (
                    <Col key={index}>
                      <Card className="h-100 shadow-sm hover-shadow" 
                        style={{ 
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          background: isPurchased 
                            ? 'linear-gradient(45deg, #28a745, #20c997)' 
                            : 'linear-gradient(45deg, #ffc107, #ff9800)',
                          color: 'white',
                          padding: '2px 8px',
                          fontSize: '12px',
                          borderBottomLeftRadius: '8px',
                          zIndex: 1
                        }}>
                          {isPurchased ? 'PURCHASED' : 'PREMIUM'}
                        </div>
                        
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-3">
                            {getFileIcon(file.name)}
                            <div className="ms-3">
                              <Card.Title className="mb-0" style={{ fontSize: '1.1rem' }}>
                                {file.name}
                              </Card.Title>
                              <small className="text-muted">Uploaded: {formatDate(file.timestamp)}</small>
                            </div>
                          </div>
                          
                          <div className="mt-auto">
                            {isPurchased ? (
                              <Button
                                variant="success"
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-100 d-flex align-items-center justify-content-center"
                                style={{ borderRadius: '8px' }}
                              >
                                <FaDownload className="me-2" /> Download Now
                              </Button>
                            ) : (
                              <>
                                <div className="text-center mb-3">
                                  <h5 className="text-warning">₹{file.price}</h5>
                                </div>
                                <Button
                                  variant="warning"
                                  className="w-100"
                                  style={{ borderRadius: '8px' }}
                                  onClick={() => initiatePayment(file)}
                                >
                                  <FaLock className="me-2" /> Purchase Now
                                </Button>
                              </>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        </Tab>
      </Tabs>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {paymentSuccess ? 'Payment Successful!' : 'Purchase Premium Content'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentSuccess ? (
            <div className="text-center py-4">
              <FaCheckCircle size={48} className="text-success mb-3" />
              <h4>Thank you for your purchase!</h4>
              <p>You can now download "{selectedFile.name}" from the premium section.</p>
              <p>A confirmation has been sent to your email.</p>
              <Button 
                variant="success" 
                onClick={() => setShowPaymentModal(false)}
                className="mt-3"
              >
                Continue
              </Button>
            </div>
          ) : (
            <>
              <h5 className="mb-3">Purchase: {selectedFile?.name}</h5>
              <p className="text-muted">Price: ₹{selectedFile?.price}</p>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Your Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your name"
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>
              </Form>
              
              <div className="alert alert-info mt-3">
                <small>
                  You will be redirected to Razorpay's secure payment gateway to complete your purchase.
                </small>
              </div>
            </>
          )}
        </Modal.Body>
        {!paymentSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePayment}>
              Proceed to Pay ₹{selectedFile?.price}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Add Razorpay script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>

      <style>{`
        .hover-shadow {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        }
      `}</style>
    </div>
  );
};

export default StudyMaterials;